import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import { message, Button, Tag, Icon, Input, Tooltip, Select, Radio, Divider } from 'antd';
import { updateScreeningGroup, fetchScreeningPackagesBulk, createScreeningGroup } from '../../../actions/screeningPackages';
import InlineGuidingTextEditor from './InlineGuidingTextEditor';
import { MAX_ATTACHMENTS, normalizeAttachmentBounds, buildTypeCodeMap } from './screeningUtils';

function getInitialCreationState() {
  return {
    isCreating: false,
    newGroupType: 'static',
    newGroupName: '',
    newGroupCode: '',
    newGroupCustomCode: false,
    newGroupCodeError: null,
    newGroupSelectedTypes: [],
    newGroupMinAtt: 0,
    newGroupMaxAtt: null,
    isCreatingSaving: false,
    newGroupBaseType: null,
    newGroupMemberGuidingText: {},
  };
}

class ScreeningGroupBrowser extends Component {
  constructor(props) {
    super(props);
    this.state = Object.assign({
      expandedGroupId: null,
      guidingTextChanges: {},   // { groupId_memberId: newText }
      groupNameChanges: {},     // { groupId: newName }
      attachmentChanges: {},    // { groupId: { min, max } }
      memberChanges: {},        // { groupId: { added: [typeCode], removed: [typeCode] } }
      editingNameGroupId: null,
      isSaving: false,
      sortColumn: 'name',
      sortDirection: 'asc',
      searchText: '',
      typeFilter: 'all',
      showFixedSave: false,
    }, getInitialCreationState());
    this.headerRef = React.createRef();
    this.sortedGroupsCache = {
      groups: null,
      sortColumn: null,
      sortDirection: null,
      result: null,
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  handleScroll = () => {
    if (!this.headerRef.current) return;
    var rect = this.headerRef.current.getBoundingClientRect();
    var isHidden = rect.bottom < 0;
    if (isHidden !== this.state.showFixedSave) {
      this.setState({ showFixedSave: isHidden });
    }
  }

  hasUnsavedChanges = () => {
    var { guidingTextChanges, groupNameChanges, attachmentChanges, memberChanges } = this.state;
    var hasMemberChanges = Object.keys(memberChanges).some(function (groupId) {
      var changes = memberChanges[groupId];
      return (changes.added && changes.added.length > 0) || (changes.removed && changes.removed.length > 0);
    });
    return (
      Object.keys(guidingTextChanges).length > 0 ||
      Object.keys(groupNameChanges).length > 0 ||
      Object.keys(attachmentChanges).length > 0 ||
      hasMemberChanges
    );
  }

  handleExpandGroup = (groupId) => {
    this.setState(function (prevState) {
      return {
        expandedGroupId: prevState.expandedGroupId === groupId ? null : groupId,
      };
    });
  }

  handleGuidingTextChange = (groupId, memberId, value) => {
    var key = groupId + '_' + memberId;
    this.setState(function (prevState) {
      var changes = Object.assign({}, prevState.guidingTextChanges);
      changes[key] = value;
      return { guidingTextChanges: changes };
    });
  }

  handleNameChange = (groupId, value) => {
    this.setState(function (prevState) {
      var changes = Object.assign({}, prevState.groupNameChanges);
      changes[groupId] = value;
      return { groupNameChanges: changes };
    });
  }

  handleAttachmentChange = (groupId, field, value) => {
    var { groups } = this.props;
    var group = groups.find(function (g) { return g.id === groupId; });
    var currentChanges = this.state.attachmentChanges[groupId] || {};
    var currentMin = currentChanges.min !== undefined ? currentChanges.min : (group ? (group.minAttachmentCount || 0) : 0);
    var currentMax = currentChanges.max !== undefined ? currentChanges.max : (group ? group.maxAttachmentCount : null);

    var newChanges = Object.assign({}, currentChanges, normalizeAttachmentBounds(currentMin, currentMax, field, value));

    var updatedAttachmentChanges = Object.assign({}, this.state.attachmentChanges);
    updatedAttachmentChanges[groupId] = newChanges;
    this.setState({ attachmentChanges: updatedAttachmentChanges });
  }

  handleAddMember = (groupId, typeCode) => {
    this.setState(function (prevState) {
      var changes = Object.assign({}, prevState.memberChanges);
      var groupChanges = Object.assign({ added: [], removed: [] }, changes[groupId]);
      // If it was previously removed, undo the removal instead
      var removedIdx = groupChanges.removed.indexOf(typeCode);
      if (removedIdx !== -1) {
        groupChanges.removed = groupChanges.removed.slice();
        groupChanges.removed.splice(removedIdx, 1);
      } else {
        groupChanges.added = groupChanges.added.concat([typeCode]);
      }
      changes[groupId] = groupChanges;
      return { memberChanges: changes };
    });
  }

  handleRemoveMember = (groupId, typeCode) => {
    this.setState(function (prevState) {
      var changes = Object.assign({}, prevState.memberChanges);
      var groupChanges = Object.assign({ added: [], removed: [] }, changes[groupId]);
      // If it was a newly added member, just remove from added list
      var addedIdx = groupChanges.added.indexOf(typeCode);
      if (addedIdx !== -1) {
        groupChanges.added = groupChanges.added.slice();
        groupChanges.added.splice(addedIdx, 1);
      } else {
        groupChanges.removed = groupChanges.removed.concat([typeCode]);
      }
      changes[groupId] = groupChanges;
      return { memberChanges: changes };
    });
  }

  handleUndoRemoveMember = (groupId, typeCode) => {
    this.setState(function (prevState) {
      var changes = Object.assign({}, prevState.memberChanges);
      var groupChanges = Object.assign({ added: [], removed: [] }, changes[groupId]);
      var removedIdx = groupChanges.removed.indexOf(typeCode);
      if (removedIdx !== -1) {
        groupChanges.removed = groupChanges.removed.slice();
        groupChanges.removed.splice(removedIdx, 1);
      }
      changes[groupId] = groupChanges;
      return { memberChanges: changes };
    });
  }

  handleStartEditName = (groupId, e) => {
    if (e) e.stopPropagation();
    this.setState({ editingNameGroupId: groupId });
  }

  handleFinishEditName = () => {
    this.setState({ editingNameGroupId: null });
  }

  handleSort = (column) => {
    var { sortColumn, sortDirection } = this.state;
    if (sortColumn === column) {
      this.setState({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      this.setState({ sortColumn: column, sortDirection: 'asc' });
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchText: e.target.value });
  }

  handleTypeFilterChange = (e) => {
    this.setState({ typeFilter: e.target.value });
  }

  // --- Creation form handlers ---

  getAvailableBaseTypes = () => {
    var { groups, organizationId, accreditationDefinitions } = this.props;
    var suffix = '-' + String(organizationId);
    // Return accreditation definitions that don't already have a dynamic group
    return (accreditationDefinitions || []).filter(function (def) {
      var expectedCode = def.typeCode + suffix;
      return !groups.some(function (g) { return g.code === expectedCode; });
    });
  }

  handleBaseTypeSelection = (typeCode) => {
    var { accreditationDefinitions } = this.props;
    var definition = (accreditationDefinitions || []).find(function (d) {
      return d.typeCode === typeCode;
    });
    var displayName = definition ? definition.typeName : typeCode;

    this.setState({
      newGroupBaseType: typeCode,
      newGroupName: displayName,
      newGroupSelectedTypes: [typeCode],
      newGroupMemberGuidingText: {},
    });
  }

  getMaxCodeLength = () => {
    var suffix = '-' + String(this.props.organizationId);
    return this.state.newGroupType === 'dynamic' ? Math.max(1, 10 - suffix.length) : 10;
  }

  generateCodeFromName = (name, maxLen) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, maxLen);
  }

  validateNewGroupCode = (code) => {
    var { groups, organizationId } = this.props;
    var { newGroupType } = this.state;

    if (!code) {
      this.setState({ newGroupCodeError: null });
      return;
    }

    var pattern = /^[a-z0-9-]+$/;
    if (!pattern.test(code)) {
      this.setState({ newGroupCodeError: 'Only lowercase letters, numbers, and hyphens allowed' });
      return;
    }

    // Build the final code to check for duplicates
    var finalCode = code;
    if (newGroupType === 'dynamic') {
      finalCode = code + '-' + String(organizationId);
    }

    var isDuplicate = groups.some(function (g) { return g.code === finalCode; });
    this.setState({
      newGroupCodeError: isDuplicate ? 'Code already exists in this organization' : null,
    });
  }

  handleNewGroupNameChange = (e) => {
    var newName = e.target.value;
    var maxLen = this.getMaxCodeLength();
    this.setState({ newGroupName: newName });

    if (!this.state.newGroupCustomCode && newName) {
      var autoCode = this.generateCodeFromName(newName, maxLen);
      this.setState({ newGroupCode: autoCode });
      this.validateNewGroupCode(autoCode);
    }
  }

  handleNewGroupCodeChange = (e) => {
    var maxLen = this.getMaxCodeLength();
    var newCode = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, maxLen);

    this.setState({
      newGroupCode: newCode,
      newGroupCustomCode: true,
    });
    this.validateNewGroupCode(newCode);
  }

  handleNewGroupTypeChange = (e) => {
    var newType = e.target.value;
    var self = this;
    this.setState({
      newGroupType: newType,
      // Reset base-type state when switching type
      newGroupBaseType: null,
      newGroupName: '',
      newGroupCode: '',
      newGroupCustomCode: false,
      newGroupCodeError: null,
      newGroupSelectedTypes: [],
      newGroupMinAtt: 0,
      newGroupMaxAtt: null,
      newGroupMemberGuidingText: {},
    }, function () {
      if (newType === 'static') {
        // Re-validate and trim code if needed
        var maxLen = self.getMaxCodeLength();
        var currentCode = self.state.newGroupCode;
        if (currentCode.length > maxLen) {
          currentCode = currentCode.substring(0, maxLen);
          self.setState({ newGroupCode: currentCode });
        }
        self.validateNewGroupCode(currentCode);
      }
    });
  }

  handleNewGroupTypesChange = (selectedTypes) => {
    this.setState({ newGroupSelectedTypes: selectedTypes });
  }

  getFilteredGroups = (groupsList, dynamicSuffix, typeCodeMap) => {
    var { searchText, typeFilter } = this.state;

    var result = groupsList;

    // Filter by type
    if (typeFilter === 'static') {
      result = result.filter(function (g) {
        return !(g.code && g.code.endsWith(dynamicSuffix));
      });
    } else if (typeFilter === 'dynamic') {
      result = result.filter(function (g) {
        return g.code && g.code.endsWith(dynamicSuffix);
      });
    }

    // Filter by search text
    if (searchText && searchText.trim()) {
      var term = searchText.trim().toLowerCase();
      result = result.filter(function (group) {
        if ((group.name || '').toLowerCase().indexOf(term) !== -1) return true;
        var members = lodash.get(group, 'screeningGroupMembers', []);
        for (var i = 0; i < members.length; i++) {
          var member = members[i];
          if ((member.type || '').toLowerCase().indexOf(term) !== -1) return true;
          var definition = typeCodeMap[member.type];
          if (definition && (definition.typeName || '').toLowerCase().indexOf(term) !== -1) return true;
        }
        return false;
      });
    }

    return result;
  }

  getSortedGroups = (groupsList) => {
    var { sortColumn, sortDirection } = this.state;

    if (
      this.sortedGroupsCache.groups === groupsList &&
      this.sortedGroupsCache.sortColumn === sortColumn &&
      this.sortedGroupsCache.sortDirection === sortDirection &&
      this.sortedGroupsCache.result
    ) {
      return this.sortedGroupsCache.result;
    }

    var sorted = groupsList.slice().sort(function (a, b) {
      var valueA, valueB;
      switch (sortColumn) {
        case 'name':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'code':
          valueA = (a.code || '').toLowerCase();
          valueB = (b.code || '').toLowerCase();
          break;
        case 'members':
          valueA = lodash.get(a, 'screeningGroupMembers', []).length;
          valueB = lodash.get(b, 'screeningGroupMembers', []).length;
          break;
        default:
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
      }
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.sortedGroupsCache = {
      groups: groupsList,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
      result: sorted,
    };

    return sorted;
  }

  handleSaveAll = () => {
    var { dispatch, groups, organizationId } = this.props;
    var { guidingTextChanges, groupNameChanges, attachmentChanges, memberChanges } = this.state;

    this.setState({ isSaving: true });

    // Collect all unique group IDs that have changes
    var changedGroupIds = {};
    Object.keys(guidingTextChanges).forEach(function (key) {
      var groupId = parseInt(key.split('_')[0], 10);
      changedGroupIds[groupId] = true;
    });
    Object.keys(groupNameChanges).forEach(function (id) {
      changedGroupIds[parseInt(id, 10)] = true;
    });
    Object.keys(attachmentChanges).forEach(function (id) {
      changedGroupIds[parseInt(id, 10)] = true;
    });
    Object.keys(memberChanges).forEach(function (id) {
      var changes = memberChanges[id];
      if ((changes.added && changes.added.length > 0) || (changes.removed && changes.removed.length > 0)) {
        changedGroupIds[parseInt(id, 10)] = true;
      }
    });

    var promises = Object.keys(changedGroupIds).map(function (groupIdStr) {
      var groupId = parseInt(groupIdStr, 10);
      var group = groups.find(function (g) { return g.id === groupId; });
      if (!group) return Promise.resolve();

      var updatedName = groupNameChanges[groupId] !== undefined
        ? groupNameChanges[groupId]
        : group.name;

      var groupMemberChanges = memberChanges[groupId] || { added: [], removed: [] };

      // Compute final members: existing minus removed, plus added
      var updatedMembers = lodash.get(group, 'screeningGroupMembers', [])
        .filter(function (member) {
          return groupMemberChanges.removed.indexOf(member.type) === -1;
        })
        .map(function (member) {
          var changeKey = groupId + '_' + member.id;
          var newText = guidingTextChanges[changeKey];
          return {
            type: member.type,
            guiding_text: newText !== undefined ? (newText || null) : (member.guidingText || null),
          };
        });

      // Add newly added members
      groupMemberChanges.added.forEach(function (typeCode) {
        updatedMembers.push({
          type: typeCode,
          guiding_text: null,
        });
      });

      var updateData = {
        name: updatedName,
        group_members: updatedMembers,
      };

      var attChanges = attachmentChanges[groupId];
      if (attChanges) {
        if (attChanges.min !== undefined) {
          updateData.min_attachment_count = attChanges.min;
        }
        if (attChanges.max !== undefined) {
          updateData.max_attachment_count = attChanges.max;
        }
      }

      return dispatch(updateScreeningGroup(groupId, updateData));
    });

    var self = this;
    Promise.all(promises)
      .then(function () {
        message.success('All changes saved successfully');
        self.setState({
          guidingTextChanges: {},
          groupNameChanges: {},
          attachmentChanges: {},
          memberChanges: {},
          editingNameGroupId: null,
          isSaving: false,
        });
      })
      .catch(function () {
        message.error('Some changes failed to save. Please try again.');
        self.setState({ isSaving: false });
      })
      .then(function () {
        dispatch(fetchScreeningPackagesBulk(organizationId));
      });
  }

  handleSaveNewGroup = () => {
    var { dispatch, organizationId } = this.props;
    var { newGroupType, newGroupName, newGroupCode, newGroupCodeError, newGroupSelectedTypes, newGroupMinAtt, newGroupMaxAtt, newGroupBaseType, newGroupMemberGuidingText } = this.state;

    // Validation
    if (!newGroupName || !newGroupName.trim()) {
      message.error('Group name is required');
      return;
    }

    // For dynamic groups, base type is required instead of code
    if (newGroupType === 'dynamic') {
      if (!newGroupBaseType) {
        message.error('Base type is required for dynamic groups');
        return;
      }
    } else {
      if (!newGroupCode || !newGroupCode.trim()) {
        message.error('Group code is required');
        return;
      }
      if (newGroupCodeError) {
        message.error(newGroupCodeError);
        return;
      }
    }

    if (newGroupSelectedTypes.length === 0) {
      message.error('At least one accreditation type is required');
      return;
    }

    var payload = {
      name: newGroupName.trim(),
      org_id: organizationId,
      group_members: newGroupSelectedTypes.map(function (typeCode) {
        var guidingText = newGroupMemberGuidingText[typeCode] || null;
        return { type: typeCode, guiding_text: guidingText };
      }),
    };

    // For dynamic groups, send base_type (backend auto-generates code)
    // For static groups, send code directly
    if (newGroupType === 'dynamic') {
      payload.base_type = newGroupBaseType;
    } else {
      payload.code = newGroupCode.trim();
    }

    if (newGroupMinAtt > 0) {
      payload.min_attachment_count = newGroupMinAtt;
    }
    if (newGroupMaxAtt !== null && newGroupMaxAtt > 0) {
      payload.max_attachment_count = newGroupMaxAtt;
    }

    this.setState({ isCreatingSaving: true });
    var self = this;

    dispatch(createScreeningGroup(payload))
      .then(function (response) {
        if (response && response.error) {
          var errorMsg = 'Failed to create screening group';
          if (response.data && response.data._errors) {
            var errors = response.data._errors;
            var messages = [];
            Object.keys(errors).forEach(function (field) {
              var err = errors[field];
              if (err && err.msg) {
                messages.push(field + ': ' + err.msg);
              } else if (typeof err === 'string') {
                messages.push(field + ': ' + err);
              }
            });
            if (messages.length > 0) {
              errorMsg = messages.join('; ');
            }
          }
          message.error(errorMsg);
          self.setState({ isCreatingSaving: false });
          return;
        }

        var wasReactivated = response.data && response.data._meta && response.data._meta.reactivated;
        if (wasReactivated) {
          message.success('Group with same code already exists, re-activating.');
        } else {
          message.success('Screening group created successfully');
        }

        // Reset form and refresh
        self.setState(getInitialCreationState());
        dispatch(fetchScreeningPackagesBulk(organizationId));
      })
      .catch(function () {
        message.error('Failed to create screening group');
        self.setState({ isCreatingSaving: false });
      });
  }

  handleCancelCreate = () => {
    this.setState(getInitialCreationState());
  }

  renderSortIcon = (column) => {
    var { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return (
        <Icon type="swap" style={{ marginLeft: '4px', color: '#bfbfbf', fontSize: '10px' }} />
      );
    }
    return (
      <Icon
        type={sortDirection === 'asc' ? 'caret-up' : 'caret-down'}
        style={{ marginLeft: '4px', color: '#1890ff', fontSize: '10px' }}
      />
    );
  }

  renderExpandedRow = (group, typeCodeMap) => {
    var { accreditationDefinitions, organizationId } = this.props;
    var { guidingTextChanges, attachmentChanges, memberChanges } = this.state;
    var members = lodash.get(group, 'screeningGroupMembers', [])
      .slice()
      .sort(function (a, b) { return (a.type || '').localeCompare(b.type || ''); });
    var attChanges = attachmentChanges[group.id] || {};
    var currentMin = attChanges.min !== undefined ? attChanges.min : (group.minAttachmentCount || 0);
    var currentMax = attChanges.max !== undefined ? attChanges.max : group.maxAttachmentCount;
    var self = this;
    var dynamicSuffix = '-' + String(organizationId);
    var isDynamic = !group.orgId && group.code && group.code.indexOf(dynamicSuffix, group.code.length - dynamicSuffix.length) !== -1;
    var isOrgSpecific = (group.orgId !== null && group.orgId !== undefined) || isDynamic;
    var groupMemberChanges = memberChanges[group.id] || { added: [], removed: [] };

    // Build the effective member list for display
    var existingTypeCodes = members.map(function (m) { return m.type; });
    var allCurrentTypes = existingTypeCodes
      .concat(groupMemberChanges.added)
      .filter(function (t) { return groupMemberChanges.removed.indexOf(t) === -1; });

    // Available types for the add dropdown (exclude already-assigned types)
    var availableTypes = (accreditationDefinitions || []).filter(function (d) {
      return allCurrentTypes.indexOf(d.typeCode) === -1;
    });

    return (
      <tr key={'expand-' + group.id}>
        <td colSpan="5" style={{ padding: '16px 24px', backgroundColor: '#fafafa' }}>
          {/* Group Configuration Section */}
          <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ marginBottom: '8px', color: '#595959', fontWeight: 'bold' }}>Group Configuration</h4>

            {/* Attachments row */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ minWidth: '120px', paddingBottom: '8px' }}>
                <strong style={{ fontSize: '14px', color: '#595959' }}>
                  Attachments
                  <Tooltip title={'Set max to 1-' + MAX_ATTACHMENTS + ' to allow attachments, or leave empty to disable. If you set a minimum, users must upload at least that many files. Max will auto-adjust if needed.'}>
                    <Icon type="info-circle" style={{ marginLeft: '6px', color: '#1890ff', cursor: 'help' }} />
                  </Tooltip>
                </strong>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                  Minimum
                </label>
                <Input
                  type="number"
                  min="0"
                  max={MAX_ATTACHMENTS}
                  value={currentMin}
                  onChange={function (e) {
                    var val = e.target.value;
                    self.handleAttachmentChange(group.id, 'min', val === '' ? '' : parseInt(val, 10));
                  }}
                  onBlur={function () {
                    var cur = self.state.attachmentChanges[group.id];
                    if (cur && (cur.min === '' || cur.min === null || isNaN(cur.min))) {
                      self.handleAttachmentChange(group.id, 'min', 0);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                  Maximum
                </label>
                <Input
                  type="number"
                  min="0"
                  max={MAX_ATTACHMENTS}
                  value={currentMax === null ? '' : currentMax}
                  onChange={function (e) {
                    var val = e.target.value;
                    self.handleAttachmentChange(group.id, 'max', val === '' ? null : parseInt(val, 10));
                  }}
                  placeholder="Leave empty or set to 0 for no attachments"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {/* Group Members Section */}
          <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ marginBottom: '12px', color: '#595959', fontWeight: 'bold' }}>Group Members</h4>

            {/* Existing members */}
            {members.map(function (member) {
              var definition = typeCodeMap[member.type];
              var typeName = definition ? definition.typeName : member.type;
              var isRemoved = groupMemberChanges.removed.indexOf(member.type) !== -1;

              return (
                <div key={member.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  marginBottom: '4px',
                  backgroundColor: isRemoved ? '#fff1f0' : '#fff',
                  borderRadius: '4px',
                  border: isRemoved ? '1px solid #ffa39e' : '1px solid #f0f0f0',
                  opacity: isRemoved ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ textDecoration: isRemoved ? 'line-through' : 'none' }}>
                      <strong>{typeName}</strong>
                    </span>
                    <Tag style={{ marginLeft: '8px' }}>{member.type}</Tag>
                    {isRemoved && <Tag color="red" style={{ marginLeft: '4px' }}>Removing</Tag>}
                  </div>
                  {isOrgSpecific && (
                    isRemoved
                      ? <Button size="small" onClick={function (e) { e.stopPropagation(); self.handleUndoRemoveMember(group.id, member.type); }}>Undo</Button>
                      : <Button size="small" type="danger" icon="delete" onClick={function (e) { e.stopPropagation(); self.handleRemoveMember(group.id, member.type); }} />
                  )}
                </div>
              );
            })}

            {/* Newly added members (pending save) */}
            {groupMemberChanges.added.map(function (typeCode) {
              var definition = typeCodeMap[typeCode];
              var typeName = definition ? definition.typeName : typeCode;

              return (
                <div key={'added-' + typeCode} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  marginBottom: '4px',
                  backgroundColor: '#f6ffed',
                  borderRadius: '4px',
                  border: '1px solid #b7eb8f',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong>{typeName}</strong>
                    <Tag style={{ marginLeft: '8px' }}>{typeCode}</Tag>
                    <Tag color="green" style={{ marginLeft: '4px' }}>New</Tag>
                  </div>
                  <Button size="small" type="danger" icon="delete" onClick={function (e) { e.stopPropagation(); self.handleRemoveMember(group.id, typeCode); }} />
                </div>
              );
            })}

            {/* Add member dropdown - only for org-specific groups */}
            {isOrgSpecific && availableTypes.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <Select
                  showSearch
                  placeholder="Add accreditation type..."
                  style={{ width: '100%', maxWidth: '400px' }}
                  value={undefined}
                  onChange={function (val) { self.handleAddMember(group.id, val); }}
                  filterOption={function (input, option) {
                    return (option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {availableTypes.map(function (def) {
                    return (
                      <Select.Option key={def.typeCode} value={def.typeCode}>
                        {def.typeName + ' (' + def.typeCode + ')'}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            )}

            {!isOrgSpecific && (
              <p style={{ color: '#999', fontSize: '12px', fontStyle: 'italic', marginTop: '8px' }}>
                Static group members are managed via the Screening Packages tab and cannot be modified here.
              </p>
            )}
          </div>

          {/* Guiding Text Configuration Section */}
          <h4 style={{ marginBottom: '12px', color: '#595959', fontWeight: 'bold' }}>Guiding Text Configuration</h4>
          {members.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic' }}>No members in this group</p>
          ) : (
            members.filter(function (member) {
              return groupMemberChanges.removed.indexOf(member.type) === -1;
            }).map(function (member) {
              var definition = typeCodeMap[member.type];
              var typeName = definition ? definition.typeName : member.type;
              var changeKey = group.id + '_' + member.id;
              var currentText = guidingTextChanges[changeKey] !== undefined
                ? guidingTextChanges[changeKey]
                : (member.guidingText || '');

              return (
                <div key={member.id} style={{
                  padding: '12px',
                  marginTop: '8px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #f0f0f0',
                }}>
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                    <strong>{typeName}</strong>
                    <Tag style={{ marginLeft: '8px' }}>{member.type}</Tag>
                  </div>
                  <InlineGuidingTextEditor
                    value={currentText}
                    onChange={function (val) { self.handleGuidingTextChange(group.id, member.id, val); }}
                    placeholder="Enter guiding text..."
                  />
                </div>
              );
            })
          )}
        </td>
      </tr>
    );
  }

  render() {
    var { groups, organizationId, filterDynamicGroups, accreditationDefinitions } = this.props;
    var { expandedGroupId, groupNameChanges, editingNameGroupId, isSaving } = this.state;
    var typeCodeMap = buildTypeCodeMap(accreditationDefinitions || []);

    // Show all groups (org-specific + global)
    // Only exclude dynamic groups when the Dynamic Groups tab is present
    var dynamicSuffix = '-' + String(organizationId);
    var allGroups = groups.filter(function (g) {
      if (filterDynamicGroups && g.code && g.code.endsWith(dynamicSuffix)) return false;
      return g.orgId === organizationId || g.orgId === null || g.orgId === undefined;
    });
    var filteredGroups = this.getFilteredGroups(allGroups, dynamicSuffix, typeCodeMap);
    var sortedGroups = this.getSortedGroups(filteredGroups);
    var hasChanges = this.hasUnsavedChanges();
    var self = this;

    var sortableHeaderStyle = {
      cursor: 'pointer',
      userSelect: 'none',
    };

    if (allGroups.length === 0 && !this.state.isCreating) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          <Icon type="inbox" style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
          <p>No screening groups found for this organization.</p>
          <Button
            type="primary"
            icon="plus"
            onClick={function () { self.setState({ isCreating: true }); }}
          >
            Create First Group
          </Button>
        </div>
      );
    }

    return (
      <div className="screening-group-browser">
        {this.state.showFixedSave && hasChanges && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            borderBottom: '1px solid #e8e8e8',
            padding: '10px 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}>
            <span style={{ marginRight: '12px', color: '#faad14', fontSize: '13px' }}>You have unsaved changes</span>
            <Button
              type="primary"
              loading={isSaving}
              onClick={this.handleSaveAll}
            >
              Save Changes
            </Button>
          </div>
        )}
        <div ref={this.headerRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Screening Groups</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              icon="plus"
              onClick={function () { self.setState({ isCreating: true }); }}
              disabled={self.state.isCreating}
            >
              Create Group
            </Button>
            <Button
              type="primary"
              disabled={!hasChanges || isSaving}
              loading={isSaving}
              onClick={this.handleSaveAll}
            >
              Save Changes
            </Button>
          </div>
        </div>
        <div style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: '6px' }}>
            <Tag color="blue" style={{ flexShrink: 0, minWidth: '70px', textAlign: 'center' }}>Static</Tag>
            <span style={{ marginLeft: '8px' }}>Pre-configured groups assigned to screening packages in the Packages tab. These define which checks are run when a package is triggered manually.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <Tag color="purple" style={{ flexShrink: 0, minWidth: '70px', textAlign: 'center' }}>Dynamic</Tag>
            <span style={{ marginLeft: '8px' }}>Groups created automatically where accreditation information is fetched from a person based on their position requirements or accreditation expiry.</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <Input.Search
            placeholder="Search by group name or member type..."
            value={this.state.searchText}
            onChange={this.handleSearchChange}
            allowClear
            style={{ maxWidth: '400px' }}
          />
          <Radio.Group value={this.state.typeFilter} onChange={this.handleTypeFilterChange} size="small">
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="static">Static</Radio.Button>
            <Radio.Button value="dynamic">Dynamic</Radio.Button>
          </Radio.Group>
        </div>

        {/* Inline group creation form */}
        {this.state.isCreating && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '16px' }}>
              <Icon type="plus-circle" style={{ marginRight: '8px', color: '#1890ff' }} />
              Create New Screening Group
            </h4>

            {/* Group Type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Group Type</label>
              <Radio.Group value={this.state.newGroupType} onChange={this.handleNewGroupTypeChange}>
                <Radio value="static">
                  <Tag color="blue">Static</Tag>
                  <span style={{ color: '#666', fontSize: '12px' }}>Assigned to packages manually</span>
                </Radio>
                <Radio value="dynamic">
                  <Tag color="purple">Dynamic</Tag>
                  <span style={{ color: '#666', fontSize: '12px' }}>Auto-triggered by position requirements</span>
                </Radio>
              </Radio.Group>
            </div>

            {/* Dynamic group: Base Type selection + pre-filled fields */}
            {this.state.newGroupType === 'dynamic' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontWeight: 'bold', minWidth: '100px' }}>
                      Base Type <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <Select
                      showSearch
                      placeholder="Select an accreditation type..."
                      value={this.state.newGroupBaseType || undefined}
                      onChange={self.handleBaseTypeSelection}
                      filterOption={function (input, option) {
                        return (option.props.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                      style={{ flex: 1, maxWidth: '400px' }}
                    >
                      {self.getAvailableBaseTypes().map(function (def) {
                        return (
                          <Select.Option key={def.typeCode} value={def.typeCode}>
                            {def.typeName + ' (' + def.typeCode + ')'}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                  <small style={{ color: '#999', fontSize: '12px', marginLeft: '100px' }}>
                    Select the accreditation type for this dynamic group. Additional members can be added after creation.
                  </small>
                </div>

                {this.state.newGroupBaseType && (
                  <div>
                    {/* Pre-filled Group Name (editable) */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={{ fontWeight: 'bold', minWidth: '100px' }}>Group Name</label>
                        <Input
                          placeholder="e.g. Working With Children Check"
                          value={this.state.newGroupName}
                          onChange={this.handleNewGroupNameChange}
                          style={{ flex: 1, maxWidth: '400px' }}
                        />
                      </div>
                    </div>

                    {/* Read-only Code Preview */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={{ fontWeight: 'bold', minWidth: '100px' }}>Group Code</label>
                        <Tag color="purple" style={{ fontSize: '13px' }}>
                          {this.state.newGroupBaseType + '-' + String(organizationId)}
                        </Tag>
                        <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>
                          (auto-generated from base type)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Static group: manual Name + Code entry */}
            {this.state.newGroupType === 'static' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontWeight: 'bold', minWidth: '100px' }}>Group Name</label>
                    <Input
                      placeholder="e.g. Working With Children Check"
                      value={this.state.newGroupName}
                      onChange={this.handleNewGroupNameChange}
                      style={{ flex: 1, maxWidth: '400px' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontWeight: 'bold', minWidth: '100px' }}>Group Code</label>
                    <Input
                      placeholder="e.g. std-checks"
                      value={this.state.newGroupCode}
                      onChange={this.handleNewGroupCodeChange}
                      style={{ flex: 1, maxWidth: '300px' }}
                      maxLength={this.getMaxCodeLength()}
                    />
                  </div>
                  {this.state.newGroupCodeError ? (
                    <small style={{ color: '#ff4d4f', fontSize: '12px', marginLeft: '100px' }}>
                      {this.state.newGroupCodeError}
                    </small>
                  ) : (
                    <small style={{ color: '#999', fontSize: '12px', marginLeft: '100px' }}>
                      {'Lowercase letters, numbers, and hyphens only (max ' + this.getMaxCodeLength() + ' chars)'}
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Accreditation Types - only for static groups; dynamic groups get types from base type */}
            {this.state.newGroupType === 'static' && (
              <div>
                <Divider style={{ margin: '12px 0' }}>Accreditation Types</Divider>

                <p style={{ marginBottom: '12px', color: '#666', fontSize: '13px' }}>
                  Select the accreditation types for this group. Individuals only need to provide one of the selected types.
                </p>

                {/* Accreditation Types Select */}
                <div style={{ marginBottom: '16px' }}>
                  <Select
                    mode="multiple"
                    placeholder="Search and select accreditation types..."
                    value={this.state.newGroupSelectedTypes}
                    onChange={this.handleNewGroupTypesChange}
                    showSearch
                    optionFilterProp="label"
                    maxTagCount={0}
                    maxTagPlaceholder={this.state.newGroupSelectedTypes.length > 0
                      ? this.state.newGroupSelectedTypes.length + ' selected'
                      : ''}
                    filterOption={function (input, option) {
                      return (option.props.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    style={{ width: '100%', maxWidth: '500px' }}
                  >
                    {(self.props.accreditationDefinitions || []).map(function (type) {
                      return (
                        <Select.Option
                          key={type.typeCode}
                          value={type.typeCode}
                          label={type.typeName + ' (' + type.typeCode + ')'}
                        >
                          {type.typeName + ' (' + type.typeCode + ')'}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>

                {/* Selected types tags */}
                {this.state.newGroupSelectedTypes.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '4px', color: '#666', fontSize: '13px' }}>
                      {'Selected (' + this.state.newGroupSelectedTypes.length + '):'}
                    </div>
                    <div>
                      {this.state.newGroupSelectedTypes.map(function (typeCode) {
                        var typeDef = (self.props.accreditationDefinitions || []).find(function (d) { return d.typeCode === typeCode; });
                        return (
                          <Tag
                            key={typeCode}
                            closable
                            onClose={function () {
                              self.setState(function (prev) {
                                return {
                                  newGroupSelectedTypes: prev.newGroupSelectedTypes.filter(function (t) { return t !== typeCode; }),
                                };
                              });
                            }}
                            style={{ marginBottom: '4px' }}
                          >
                            {typeDef ? typeDef.typeName : typeCode}
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Attachment Configuration */}
            <Divider style={{ margin: '12px 0' }}>Attachment Configuration (Optional)</Divider>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                  Min Attachments
                </label>
                <Input
                  type="number"
                  min="0"
                  max={MAX_ATTACHMENTS}
                  value={this.state.newGroupMinAtt}
                  onChange={function (e) {
                    var val = e.target.value;
                    self.setState({ newGroupMinAtt: val === '' ? 0 : Math.min(parseInt(val, 10) || 0, MAX_ATTACHMENTS) });
                  }}
                  style={{ width: '120px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                  Max Attachments
                </label>
                <Input
                  type="number"
                  min="0"
                  max={MAX_ATTACHMENTS}
                  value={this.state.newGroupMaxAtt === null ? '' : this.state.newGroupMaxAtt}
                  onChange={function (e) {
                    var val = e.target.value;
                    self.setState({ newGroupMaxAtt: val === '' ? null : Math.min(parseInt(val, 10) || 0, MAX_ATTACHMENTS) });
                  }}
                  placeholder="Leave empty for none"
                  style={{ width: '180px' }}
                />
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button onClick={this.handleCancelCreate} disabled={this.state.isCreatingSaving}>
                Cancel
              </Button>
              <Button type="primary" onClick={this.handleSaveNewGroup} loading={this.state.isCreatingSaving}>
                Save Group
              </Button>
            </div>
          </div>
        )}

        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th style={{ width: '5%' }}></th>
              <th
                style={Object.assign({}, sortableHeaderStyle, { width: '35%' })}
                onClick={function () { self.handleSort('name'); }}
              >
                Group Name
                {this.renderSortIcon('name')}
              </th>
              <th
                style={Object.assign({}, sortableHeaderStyle, { width: '25%' })}
                onClick={function () { self.handleSort('code'); }}
              >
                Code
                {this.renderSortIcon('code')}
              </th>
              <th
                style={Object.assign({}, sortableHeaderStyle, { width: '15%' })}
                onClick={function () { self.handleSort('members'); }}
              >
                Members
                {this.renderSortIcon('members')}
              </th>
              <th style={{ width: '20%' }}>Attachments</th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map(function (group) {
              var isExpanded = expandedGroupId === group.id;
              var memberCount = lodash.get(group, 'screeningGroupMembers', []).length;
              var displayName = groupNameChanges[group.id] !== undefined
                ? groupNameChanges[group.id]
                : group.name;
              var isEditingName = editingNameGroupId === group.id;
              var minAtt = group.minAttachmentCount || 0;
              var maxAtt = group.maxAttachmentCount;
              var attDisplay = maxAtt !== null && maxAtt !== undefined && maxAtt > 0
                ? minAtt + '-' + maxAtt
                : '0';

              var rows = [
                <tr
                  key={group.id}
                  onClick={function () { self.handleExpandGroup(group.id); }}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ textAlign: 'center' }}>
                    <Icon
                      type={isExpanded ? 'down' : 'right'}
                      style={{ fontSize: '12px', color: '#999' }}
                    />
                  </td>
                  <td>
                    {isEditingName ? (
                      <Input
                        value={displayName}
                        onChange={function (e) { self.handleNameChange(group.id, e.target.value); }}
                        onBlur={function () { self.handleFinishEditName(); }}
                        onPressEnter={function () { self.handleFinishEditName(); }}
                        onClick={function (e) { e.stopPropagation(); }}
                        autoFocus
                        size="small"
                        style={{ width: '200px' }}
                      />
                    ) : (
                      <span>
                        {displayName}
                        {group.code && group.code.endsWith(dynamicSuffix)
                          ? <Tag color="purple" style={{ marginLeft: '8px' }}>Dynamic</Tag>
                          : <Tag color="blue" style={{ marginLeft: '8px' }}>Static</Tag>
                        }
                        <Icon
                          type="edit"
                          onClick={function (e) { self.handleStartEditName(group.id, e); }}
                          style={{ marginLeft: '8px', color: '#1890ff', cursor: 'pointer', fontSize: '12px' }}
                        />
                      </span>
                    )}
                  </td>
                  <td>
                    <Tag color="blue">{group.code}</Tag>
                  </td>
                  <td>
                    {memberCount + ' ' + (memberCount === 1 ? 'member' : 'members')}
                  </td>
                  <td>{attDisplay}</td>
                </tr>,
              ];

              if (isExpanded) {
                rows.push(self.renderExpandedRow(group, typeCodeMap));
              }

              return rows;
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

ScreeningGroupBrowser.propTypes = {
  groups: PropTypes.array.isRequired,
  organizationId: PropTypes.number.isRequired,
  accreditationDefinitions: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  filterDynamicGroups: PropTypes.bool,
};

export default ScreeningGroupBrowser;
